import { useSnackbar } from 'notistack';
import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { SxProps, Theme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';

import { useTranslate } from 'src/locales';
import { generateUploadError } from 'src/services/files/file.utils';
import { useUploadFileMutation } from 'src/services/files/file.service';

import Iconify from 'src/components/iconify';
import { CroppedImageOptions } from 'src/components/image-cropper/cropper-utils';

import AvatarDisplay from './avatar-display';
import SquareCropDialog from './square-crop-dialog';

// ----------------------------------------------------------------------

const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];
/** Guard on the raw input; the cropped output is separately capped to ~1MB. */
const MAX_INPUT_BYTES = 20 * 1024 * 1024;

/** Camera-overlay corner radius per avatar variant. */
const OVERLAY_RADIUS: Record<'circular' | 'rounded' | 'square', string | number> = {
  circular: '50%',
  rounded: 1,
  square: 0,
};

export interface EntityAvatarEditableProps {
  src?: string | null;
  name?: string | null;
  size?: number;
  variant?: 'circular' | 'rounded' | 'square';
  outputFormat?: 'jpeg' | 'png';
  fallbackIcon?: string;
  cropTitle?: string;
  onChange?: (fileId: string | null, publicUrl: string | null) => void | Promise<void>;
  sx?: SxProps<Theme>;
}

/**
 * The editable half of EntityAvatar (JT1-1539): the pure AvatarDisplay plus a camera affordance —
 * shown on hover on desktop, always on touch — that opens a crop-to-square dialog, uploads the ~1MB
 * result and reports it via onChange. Split out so the display-only path stays free of the RTK/upload
 * hooks (and thus needs no Redux provider in list rows).
 */
export default function EntityAvatarEditable({
  src,
  name,
  size = 40,
  variant = 'circular',
  outputFormat = 'jpeg',
  fallbackIcon,
  cropTitle,
  onChange,
  sx,
}: EntityAvatarEditableProps) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const [uploadFile] = useUploadFileMutation();

  const inputRef = useRef<HTMLInputElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Immediate-feedback override until the parent's refetched `src` arrives; `undefined` = no override.
  const [override, setOverride] = useState<string | null | undefined>(undefined);

  // Once the persisted src prop changes, drop the local override so we track the source of truth.
  useEffect(() => setOverride(undefined), [src]);

  const displaySrc = override !== undefined ? override : src ?? undefined;

  const closeCrop = useCallback(() => {
    setCropSrc((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset so selecting the same file again still fires onChange.
      event.target.value = '';
      if (!file) return;

      if (!ACCEPTED_TYPES.includes(file.type)) {
        enqueueSnackbar(t('upload.api.create.invalid_file_type'), { variant: 'error' });
        return;
      }
      if (file.size > MAX_INPUT_BYTES) {
        enqueueSnackbar(t('components.entity_avatar.file_too_large'), { variant: 'error' });
        return;
      }

      setCropSrc(URL.createObjectURL(file));
    },
    [enqueueSnackbar, t]
  );

  const cropOptions: CroppedImageOptions = { format: outputFormat };

  const handleCropped = useCallback(
    async (file: File) => {
      setSaving(true);
      try {
        const uploaded = await uploadFile({ file, type: 'image' }).unwrap();
        setOverride(uploaded.public_path);
        await onChange?.(uploaded.id, uploaded.public_path);
        closeCrop();
      } catch (e) {
        enqueueSnackbar(generateUploadError(e), { variant: 'error' });
      } finally {
        setSaving(false);
      }
    },
    [uploadFile, onChange, closeCrop, enqueueSnackbar]
  );

  const handleRemove = useCallback(async () => {
    setMenuAnchor(null);
    try {
      setOverride(null);
      await onChange?.(null, null);
    } catch (e) {
      enqueueSnackbar(generateUploadError(e), { variant: 'error' });
    }
  }, [onChange, enqueueSnackbar]);

  const overlayRadius = OVERLAY_RADIUS[variant];

  return (
    <Box
      sx={[
        {
          position: 'relative',
          width: size,
          height: size,
          flexShrink: 0,
          '& .entity-avatar-camera': { opacity: 0, transition: 'opacity 0.2s' },
          '&:hover .entity-avatar-camera': { opacity: 1 },
          // Touch devices have no hover — keep the affordance permanently visible.
          '@media (hover: none)': { '& .entity-avatar-camera': { opacity: 1 } },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <AvatarDisplay src={displaySrc} name={name} size={size} variant={variant} fallbackIcon={fallbackIcon} />

      <Box
        className="entity-avatar-camera"
        role="button"
        aria-label={t('components.entity_avatar.change_photo')}
        tabIndex={0}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setMenuAnchor(e.currentTarget);
        }}
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: 'common.white',
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          borderRadius: overlayRadius,
        }}
      >
        {saving ? (
          <CircularProgress size={size * 0.4} sx={{ color: 'common.white' }} />
        ) : (
          <Iconify icon="solar:camera-add-bold" width={size * 0.4} />
        )}
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        hidden
        onChange={handleFileChange}
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            inputRef.current?.click();
          }}
        >
          <Iconify icon="solar:gallery-add-bold" sx={{ mr: 1 }} />
          {displaySrc
            ? t('components.entity_avatar.change_photo')
            : t('components.entity_avatar.upload_photo')}
        </MenuItem>

        {displaySrc && (
          <MenuItem onClick={handleRemove} sx={{ color: 'error.main' }}>
            <Iconify icon="solar:trash-bin-trash-bold" sx={{ mr: 1 }} />
            {t('components.entity_avatar.remove_photo')}
          </MenuItem>
        )}
      </Menu>

      {cropSrc && (
        <SquareCropDialog
          open={Boolean(cropSrc)}
          imageSrc={cropSrc}
          title={cropTitle}
          cropOptions={cropOptions}
          saving={saving}
          onClose={closeCrop}
          onCropped={handleCropped}
        />
      )}
    </Box>
  );
}
